import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronLeft, ChevronRight, Check, Users, Sparkles, Shield, Zap, AlertCircle, Mail, Info, Calendar, Fingerprint, Globe, Phone, X } from 'lucide-react';
import API from '@/lib/api';
import { toast } from 'sonner';
import { AddressAutocomplete } from '@/components/AddressAutocomplete';

const BR_TYPES = [
  { code: 'US', name: 'United States', regType: 'EIN', placeholder: '12-3456789' },
  { code: 'UK', name: 'United Kingdom', regType: 'CRN', placeholder: '12345678' },
  { code: 'AU', name: 'Australia', regType: 'ABN', placeholder: '12 345 678 901' },
  { code: 'CA', name: 'Canada', regType: 'BN', placeholder: '123456789RC0001' },
  { code: 'NZ', name: 'New Zealand', regType: 'NZBN', placeholder: '9429000000000' },
  { code: 'DE', name: 'Germany', regType: 'HRB', placeholder: 'HRB 123456' },
  { code: 'FR', name: 'France', regType: 'SIRET', placeholder: '123 456 789 00012' },
  { code: 'IE', name: 'Ireland', regType: 'CRO', placeholder: '123456' },
  { code: 'NL', name: 'Netherlands', regType: 'KVK', placeholder: '12345678' },
  { code: 'SG', name: 'Singapore', regType: 'UEN', placeholder: '201234567A' },
  { code: 'JP', name: 'Japan', regType: 'Corp No', placeholder: '1234567890123' },
  { code: 'IT', name: 'Italy', regType: 'P.IVA', placeholder: '12345678901' },
  { code: 'ES', name: 'Spain', regType: 'NIF', placeholder: 'B12345678' },
  { code: 'BE', name: 'Belgium', regType: 'BCE', placeholder: '0123.456.789' },
  { code: 'AT', name: 'Austria', regType: 'FN', placeholder: '123456a' },
  { code: 'SE', name: 'Sweden', regType: 'Org.nr', placeholder: '123456-1234' },
  { code: 'DK', name: 'Denmark', regType: 'CVR', placeholder: '12345678' },
  { code: 'PT', name: 'Portugal', regType: 'NIPC', placeholder: '123456789' },
  { code: 'FI', name: 'Finland', regType: 'Y-tunnus', placeholder: '1234567-8' },
  { code: 'IN', name: 'India', regType: 'CIN', placeholder: 'U12345MH2020PTC123456' },
];

const AGENCY_INDUSTRIES = [
  'Advertising', 'Talent Management', 'Fashion & Beauty', 'Sports & Fitness',
  'Technology', 'Entertainment', 'Healthcare & Wellness', 'Finance',
  'Food & Beverage', 'Automotive', 'Travel & Hospitality', 'E-commerce',
  'Gaming', 'Education', 'Real Estate', 'Other',
];

const REFERRAL_SOURCES = [
  { value: 'search', label: 'Search engine (Google, etc.)' },
  { value: 'social', label: 'Social media' },
  { value: 'referral', label: 'Referred by someone' },
  { value: 'event', label: 'Conference or event' },
  { value: 'news', label: 'News article or blog' },
  { value: 'ad', label: 'Online ad' },
  { value: 'other', label: 'Other' },
];

const PASSWORD_RULES = [
  { key: 'len',   label: 'At least 8 characters',       test: (v) => v.length >= 8 },
  { key: 'upper', label: 'One uppercase letter',        test: (v) => /[A-Z]/.test(v) },
  { key: 'lower', label: 'One lowercase letter',        test: (v) => /[a-z]/.test(v) },
  { key: 'num',   label: 'One number',                  test: (v) => /[0-9]/.test(v) },
  { key: 'sym',   label: 'One special character',       test: (v) => /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/'`~]/.test(v) },
];

const stepVariants = {
  enter: (dir) => ({ x: dir > 0 ? 40 : -40, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -40 : 40, opacity: 0 }),
};

export default function SignupWizard({ onSuccess, onSwitchToLogin }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [loading, setLoading] = useState(false);
  const [mockCode, setMockCode] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [createdUser, setCreatedUser] = useState(null);
  const [form, setForm] = useState({
    role: '',
    display_name: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    address: null, // { raw, verified, formatted_address, postal_address }
    email: '',
    password: '',
    confirm: '',
    agency_name: '',
    agency_country: '',
    business_registration_number: '',
    business_registration_type: '',
    agency_website: '',
    agency_phone: '',
    agency_industry: [],
    agency_description: '',
    referral_source: '',
    terms_accepted: false,
    marketing_opt_in: true,
  });

  const isAgency = form.role === 'buyer';
  const isCreator = form.role === 'creator';
  // Steps (both roles, 5 total): 0 Role | 1 Basics | 2 Role-specific | 3 Terms | 4 Verify
  //   step 2 = Agency details (agency) OR Personal details: DOB + address (creator)
  const visibleSteps = 5;

  const go = (d) => { setDirection(d); setStep(s => s + d); };

  // Age from an ISO "YYYY-MM-DD" date
  const ageFrom = (iso) => {
    if (!iso) return null;
    const dob = new Date(iso);
    if (isNaN(dob.getTime())) return null;
    const t = new Date();
    let a = t.getFullYear() - dob.getFullYear();
    const m = t.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < dob.getDate())) a--;
    return a;
  };
  const dobAge = ageFrom(form.date_of_birth);

  // ── Validation per step
  const canNext = (() => {
    if (step === 0) return !!form.role;
    if (step === 1) {
      const passOK = PASSWORD_RULES.every(r => r.test(form.password));
      return form.first_name.trim() && form.last_name.trim() && form.email.includes('@') && passOK && form.password === form.confirm;
    }
    if (step === 2 && isAgency) {
      const hasWebsite = form.agency_website.trim() && /^https?:\/\/.+\..+/.test(form.agency_website.trim());
      const hasPhone = form.agency_phone.trim().length >= 6;
      return form.agency_name.trim() && form.agency_country && hasWebsite && hasPhone;
    }
    if (step === 2 && isCreator) return dobAge !== null && dobAge >= 18 && (!!form.address?.verified || !!form.address?.manual_entry);
    if (step === 3) return form.terms_accepted;
    return true;
  })();

  const submit = async () => {
    setLoading(true);
    try {
      const display_name = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
      const payload = {
        email: form.email, password: form.password,
        role: form.role, display_name,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        terms_accepted: form.terms_accepted,
        marketing_opt_in: form.marketing_opt_in,
      };
      if (isCreator) {
        payload.date_of_birth = form.date_of_birth;
        payload.address = form.address;
      }
      if (isAgency) {
        const country = BR_TYPES.find(b => b.code === form.agency_country);
        Object.assign(payload, {
          agency_name: form.agency_name,
          agency_country: form.agency_country,
          business_registration_number: form.business_registration_number,
          business_registration_type: country?.regType || '',
          agency_website: form.agency_website.trim(),
          agency_phone: form.agency_phone.trim(),
          agency_industry: form.agency_industry,
          agency_description: form.agency_description.trim(),
          referral_source: form.referral_source,
        });
      }
      const res = await API.post('/auth/signup', payload);
      // Store token + user; do NOT redirect yet — wait for email verification
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
        setAuthToken(res.data.token);
      }
      setCreatedUser(res.data.user);
      setMockCode(res.data.verification_code_mock || '');
      toast.success('Account created — check your email for the code');
      setDirection(1); setStep(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  const handleVerified = async (verifiedUser) => {
    onSuccess({ user: verifiedUser || createdUser, token: authToken });
  };

  // Stepper index (both roles now share the same 5-step layout)
  const stepIndex = step;

  return (
    <div className="w-full max-w-lg" data-testid="signup-wizard">
      {/* Stepper */}
      <div className="flex items-center justify-center gap-1.5 mb-7">
        {Array.from({ length: visibleSteps - 1 }).map((_, i) => (
          <div key={i} className={`h-1 rounded-full transition-all ${stepIndex > i ? 'bg-blue-600 w-8' : stepIndex === i ? 'bg-blue-600 w-12' : 'bg-slate-200 w-6'}`} />
        ))}
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        {/* ── Step 0: Role Picker ── */}
        {step === 0 && (
          <motion.div key="role" custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.25 }} className="space-y-5">
            <div>
              <h2 className="text-3xl font-semibold text-slate-900">Join Mylikeness</h2>
              <p className="text-slate-500 mt-1 text-sm">Who are you signing up as?</p>
            </div>
            <div className="grid gap-3">
              <button type="button" data-testid="role-creator"
                onClick={() => setForm(f => ({ ...f, role: 'creator' }))}
                className={`text-left p-5 rounded-2xl border-2 transition-all ${form.role === 'creator' ? 'bg-blue-50 border-blue-600' : 'bg-white border-slate-200 hover:border-blue-300'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${form.role === 'creator' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600'}`}>
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">I'm a Creator</p>
                    <p className="text-xs text-slate-500 mt-0.5">License my likeness to agencies. Receive proposals, negotiate terms, earn royalties.</p>
                  </div>
                  {form.role === 'creator' && <Check className="w-4 h-4 text-blue-600 mt-1" />}
                </div>
              </button>
              <button type="button" data-testid="role-agency"
                onClick={() => setForm(f => ({ ...f, role: 'buyer' }))}
                className={`text-left p-5 rounded-2xl border-2 transition-all ${form.role === 'buyer' ? 'bg-purple-50 border-purple-600' : 'bg-white border-slate-200 hover:border-purple-300'}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${form.role === 'buyer' ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-600'}`}>
                    <Users className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">I'm an Agency</p>
                    <p className="text-xs text-slate-500 mt-0.5">Commission creators. Build campaigns, manage licensing, run generative content at scale.</p>
                  </div>
                  {form.role === 'buyer' && <Check className="w-4 h-4 text-purple-600 mt-1" />}
                </div>
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Step 1: Basics ── */}
        {step === 1 && (
          <motion.div key="basics" custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.25 }} className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Create your account</h2>
              <p className="text-slate-500 text-sm mt-1">We'll use this to contact you and secure your account.</p>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">First name</Label>
                  <Input data-testid="signup-first-name" value={form.first_name}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="Jane" className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Last name</Label>
                  <Input data-testid="signup-last-name" value={form.last_name}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Smith" className="h-10 mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Email</Label>
                <Input data-testid="signup-email" type="email" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="you@domain.com" className="h-10 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Password</Label>
                <Input data-testid="signup-password" type="password" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="••••••••" className="h-10 mt-1" />
                {form.password && (
                  <div className="mt-2 space-y-1">
                    {PASSWORD_RULES.map(r => {
                      const ok = r.test(form.password);
                      return (
                        <div key={r.key} className="flex items-center gap-1.5 text-[11px]">
                          <div className={`w-3 h-3 rounded-full flex items-center justify-center ${ok ? 'bg-emerald-500 text-white' : 'bg-slate-200'}`}>
                            {ok && <Check className="w-2 h-2" />}
                          </div>
                          <span className={ok ? 'text-emerald-700' : 'text-slate-400'}>{r.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-600">Confirm password</Label>
                <Input data-testid="signup-confirm" type="password" value={form.confirm}
                  onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                  placeholder="••••••••" className="h-10 mt-1" />
                {form.confirm && form.password !== form.confirm && (
                  <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Passwords don't match</p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Step 2 (Agency only): Business details ── */}
        {step === 2 && isAgency && (
          <motion.div key="agency-details" custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.25 }} className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">About your agency</h2>
              <p className="text-slate-500 text-sm mt-1">Help creators understand who you are — this builds trust and improves proposal acceptance rates.</p>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Agency name <span className="text-red-400">*</span></Label>
                  <Input data-testid="signup-agency-name" value={form.agency_name}
                    onChange={e => setForm(f => ({ ...f, agency_name: e.target.value }))}
                    placeholder="Acme Agency" className="h-10 mt-1" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Country <span className="text-red-400">*</span></Label>
                  <Select value={form.agency_country} onValueChange={v => setForm(f => ({ ...f, agency_country: v }))}>
                    <SelectTrigger data-testid="signup-agency-country" className="h-10 mt-1"><SelectValue placeholder="Select country..." /></SelectTrigger>
                    <SelectContent className="bg-white max-h-60">
                      {BR_TYPES.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-600">Website <span className="text-red-400">*</span></Label>
                  <div className="relative">
                    <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="signup-agency-website" value={form.agency_website}
                      onChange={e => setForm(f => ({ ...f, agency_website: e.target.value }))}
                      placeholder="https://acme.com" className="h-10 mt-1 pl-9" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Phone <span className="text-red-400">*</span></Label>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="signup-agency-phone" value={form.agency_phone}
                      onChange={e => setForm(f => ({ ...f, agency_phone: e.target.value }))}
                      placeholder="+1 555 123 4567" className="h-10 mt-1 pl-9" />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-600">Industry <span className="text-[10px] text-slate-400 font-normal">(select all that apply)</span></Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="signup-agency-industry">
                  {AGENCY_INDUSTRIES.map(ind => {
                    const selected = form.agency_industry.includes(ind);
                    return (
                      <button key={ind} type="button" data-testid={`industry-chip-${ind.toLowerCase().replace(/[^a-z]/g, '-')}`}
                        onClick={() => setForm(f => ({
                          ...f,
                          agency_industry: selected
                            ? f.agency_industry.filter(i => i !== ind)
                            : [...f.agency_industry, ind]
                        }))}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                          selected
                            ? 'bg-purple-100 border-purple-300 text-purple-700'
                            : 'bg-white border-slate-200 text-slate-500 hover:border-purple-200 hover:text-purple-600'
                        }`}>
                        {selected && <Check className="w-3 h-3 inline mr-1" />}{ind}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-600">Short description <span className="text-[10px] text-slate-400 font-normal">(optional, max 200 chars)</span></Label>
                <Textarea data-testid="signup-agency-description" value={form.agency_description}
                  onChange={e => { if (e.target.value.length <= 200) setForm(f => ({ ...f, agency_description: e.target.value })); }}
                  placeholder="What does your agency do? e.g. 'Full-service digital agency specialising in creator-led campaigns for beauty and wellness brands.'"
                  className="mt-1 text-sm" rows={2} />
                <p className="text-[10px] text-slate-400 mt-0.5 text-right">{form.agency_description.length}/200</p>
              </div>

              {form.agency_country && (
                <div>
                  <Label className="text-xs text-slate-600 flex items-center gap-2">
                    {BR_TYPES.find(c => c.code === form.agency_country)?.regType} Number
                    <span className="text-[10px] font-normal text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Optional</span>
                  </Label>
                  <Input data-testid="signup-business-reg" value={form.business_registration_number}
                    onChange={e => setForm(f => ({ ...f, business_registration_number: e.target.value }))}
                    placeholder={BR_TYPES.find(c => c.code === form.agency_country)?.placeholder}
                    className="h-10 mt-1" />
                  <p className="text-[10px] text-slate-400 mt-1">You can add this later from Settings if you don&apos;t have it handy.</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ── Step 2 (Creator only): Personal details — DOB + verified address ── */}
        {step === 2 && isCreator && (
          <motion.div key="creator-details" custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.25 }} className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">A few personal details</h2>
              <p className="text-slate-500 text-sm mt-1">We need these to verify your identity and pay you correctly. They're kept private.</p>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-400" /> Date of birth</Label>
                <Input data-testid="signup-dob" type="date"
                  value={form.date_of_birth}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
                  className="h-10 mt-1" />
                {form.date_of_birth && dobAge !== null && dobAge < 18 && (
                  <p className="text-[11px] text-red-500 mt-1 flex items-center gap-1" data-testid="signup-dob-error">
                    <AlertCircle className="w-3 h-3" /> You must be at least 18 to create a creator account.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-600 flex items-center gap-1.5"><Info className="w-3.5 h-3.5 text-slate-400" /> Residential address</Label>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(addr) => setForm(f => ({ ...f, address: addr }))}
                  testId="signup-address"
                />
              </div>
              <div className="flex items-start gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <Shield className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                <p className="text-[11px] text-slate-500 leading-relaxed">Your address is checked against a global address database to confirm it's real. We never share it publicly.</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Terms & Submit ── */}
        {step === 3 && (
          <motion.div key="terms" custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit"
            transition={{ duration: 0.25 }} className="space-y-4">
            <div>
              <h2 className="text-2xl font-semibold text-slate-900">Almost there</h2>
              <p className="text-slate-500 text-sm mt-1">Review and accept our terms to create your account.</p>
            </div>
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-blue-300 bg-white">
                <input type="checkbox" data-testid="signup-terms"
                  checked={form.terms_accepted}
                  onChange={e => setForm(f => ({ ...f, terms_accepted: e.target.checked }))}
                  className="mt-0.5 rounded border-slate-300" />
                <span className="text-xs text-slate-700 flex-1">
                  I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a>, <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>, and <a href="/acceptable-use" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Acceptable Use Policy</a>.
                  <br /><span className="text-[10px] text-slate-400 mt-1 block">You can&apos;t create an account without agreeing to these.</span>
                </span>
              </label>

              {/* Plain-English summary of key clauses so agreement is truly informed */}
              <details className="rounded-xl border border-slate-200 bg-white" data-testid="signup-terms-preview">
                <summary className="cursor-pointer px-4 py-3 text-xs font-semibold text-slate-700 flex items-center justify-between hover:bg-slate-50 rounded-xl">
                  <span>What you&apos;re agreeing to (plain English)</span>
                  <span className="text-[10px] text-slate-400">Tap to expand</span>
                </summary>
                <div className="px-4 py-3 border-t border-slate-100 max-h-56 overflow-y-auto text-[11px] text-slate-600 leading-relaxed space-y-2">
                  <p><strong>Marketplace, not the licensor.</strong> Mylikeness connects creators and agencies and runs the escrow — but the actual likeness license is a direct contract between you and the counter-party, signed inside the app.</p>
                  <p><strong>18+ only.</strong> You confirm you&apos;re over eighteen with legal capacity to sign contracts.</p>
                  <p><strong>Your rules, enforced.</strong> Creators keep veto power over every piece of content before it&apos;s released. Agencies must stay within the platforms, territory, exclusivity, duration, and ban categories agreed to.</p>
                  <p><strong>Escrow-secured payments.</strong> Agencies fund deals up front. Funds sit in Stripe-managed escrow and only release to the creator after approval — no chasing invoices.</p>
                  <p><strong>Platform fee.</strong> A platform fee is charged on each released deal, disclosed before funding. Stripe processing fees are separate.</p>
                  <p><strong>No off-platform circumvention.</strong> Users introduced through the platform can&apos;t take the deal elsewhere to dodge fees — doing so triggers a recovery charge and account suspension.</p>
                  <p><strong>Prohibited content.</strong> No minors, no non-consensual intimate imagery, no political deepfakes, no defamation, no unauthorized third-party likeness. AI-generated deliverables must be clearly labelled as such when published.</p>
                  <p><strong>Australian law.</strong> New South Wales law governs. Disputes go to good-faith negotiation, then mediation via the Australian Disputes Centre. Consumer guarantees under the Australian Consumer Law are unaffected.</p>
                  <p><strong>Data.</strong> We use Stripe (payments + identity), Resend (email), Google (address validation), and Emergent (hosting). Full detail in the Privacy Policy.</p>
                  <p className="text-slate-400 text-[10px] italic mt-2">This summary is provided for convenience. The full Terms of Service, Privacy Policy and Acceptable Use Policy are the binding documents.</p>
                </div>
              </details>
              <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 cursor-pointer hover:border-slate-300 bg-white">
                <input type="checkbox" data-testid="signup-marketing"
                  checked={form.marketing_opt_in}
                  onChange={e => setForm(f => ({ ...f, marketing_opt_in: e.target.checked }))}
                  className="mt-0.5 rounded border-slate-300" />
                <span className="text-xs text-slate-700 flex-1">
                  Email me product updates and occasional tips.
                  <br /><span className="text-[10px] text-slate-400 mt-1 block">Optional. Unsubscribe anytime.</span>
                </span>
              </label>
            </div>
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
              <div className="flex items-start gap-2">
                <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
                <p className="text-[11px] text-blue-800">We&apos;ll send a 6-digit code to <strong>{form.email}</strong> to verify your email address.</p>
              </div>
            </div>
            {isAgency && (
              <div>
                <Label className="text-xs text-slate-600">How did you hear about Mylikeness? <span className="text-[10px] text-slate-400 font-normal">(optional)</span></Label>
                <Select value={form.referral_source} onValueChange={v => setForm(f => ({ ...f, referral_source: v }))}>
                  <SelectTrigger data-testid="signup-referral" className="h-10 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {REFERRAL_SOURCES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isCreator && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="identity-verification-notice">
                <div className="flex items-start gap-2">
                  <Fingerprint className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-amber-800 leading-relaxed">
                    <strong>Identity verification required.</strong> To keep the marketplace trusted and safe, you'll be asked to verify your identity (a quick government-ID check) before your account is fully activated. We'll guide you through it after signup.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ── Final: Email Verification ── */}
        {step === 4 && (
          <VerificationStep email={form.email} mockCode={mockCode} onVerified={handleVerified} />
        )}
      </AnimatePresence>

      {/* ── Action Bar ── */}
      {step < 4 && (
        <div className="flex items-center justify-between mt-7">
          {step > 0 ? (
            <Button variant="ghost" size="sm" onClick={() => go(-1)} data-testid="signup-back"
              className="text-slate-500 hover:text-slate-700">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          ) : (
            <button type="button" onClick={onSwitchToLogin} className="text-xs text-slate-500 hover:text-slate-700">
              Already have an account? <span className="text-blue-600 underline">Sign in</span>
            </button>
          )}

          {step < 3 ? (
            <Button disabled={!canNext} onClick={() => go(1)} data-testid="signup-next"
              className="bg-slate-900 hover:bg-slate-800 text-white">
              Continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button disabled={!canNext || loading} onClick={submit} data-testid="signup-submit"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              {loading ? 'Creating…' : <>Create Account <Zap className="w-4 h-4 ml-1" /></>}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Verification step: 6-digit code entry + resend ──
function VerificationStep({ email, mockCode, onVerified }) {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [currentMock, setCurrentMock] = useState(mockCode || '');

  const verify = async () => {
    if (code.length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setVerifying(true);
    try {
      const res = await API.post('/auth/verify-email', { email, code });
      toast.success('Email verified!');
      onVerified(res.data.user);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setVerifying(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      const res = await API.post('/auth/resend-verification', { email });
      setCurrentMock(res.data.verification_code_mock || '');
      toast.success('New code sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resend failed');
    } finally { setResending(false); }
  };

  return (
    <motion.div key="verify" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-2" data-testid="verification-step">
      <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
        <Mail className="w-6 h-6 text-blue-600" />
      </div>
      <h2 className="text-xl font-semibold text-slate-900">Check your email</h2>
      <p className="text-slate-500 text-sm mt-2">
        We sent a 6-digit code to<br /><strong className="text-slate-800">{email}</strong>
      </p>

      {currentMock && (
        <div className="mt-4 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
          <p className="text-amber-800 text-xs">
            <strong>DEV MODE:</strong> Code is <code className="font-mono text-amber-900 font-bold">{currentMock}</code>
          </p>
        </div>
      )}

      <div className="mt-6">
        <Input
          data-testid="verify-code-input"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="000000"
          maxLength={6}
          className="text-center font-mono text-2xl tracking-[0.5em] h-14"
        />
      </div>

      <Button onClick={verify} disabled={verifying || code.length !== 6}
        data-testid="verify-submit-btn"
        className="w-full mt-4 h-11 bg-blue-600 hover:bg-blue-700 text-white">
        {verifying ? 'Verifying…' : 'Verify Email'}
      </Button>

      <button type="button" onClick={resend} disabled={resending}
        data-testid="verify-resend-btn"
        className="mt-3 text-xs text-slate-500 hover:text-slate-700">
        {resending ? 'Sending…' : "Didn't get the code? Resend"}
      </button>
    </motion.div>
  );
}
